import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { SearchInput, Table as UITable } from '@onefootprint/ui';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce, useUpdateEffect } from 'usehooks-ts';
import useMobileLayout from '@/hooks/use-mobile-layout';
import { useEntitiesContext } from '../list/hooks/use-entities-context';
import Filters from './components/filters';
import MobileList from './components/mobile-list';

// Below this length, the backend's `vaults_matching_search` skips the plaintext search, so a
// partial query can only ever match an exact fingerprint.
const MIN_SEARCH_LENGTH = 4;
export type TableProps = {
  'aria-label': string;
  columns: {
    width: string;
    text: string;
  }[];
  emptyStateText: string;
  renderTr: (entity: Entity) => JSX.Element;
  /** Phones list each entity as a tappable card, since a three-column table would scroll the status off-screen. */
  renderMobileRow?: (entity: Entity) => JSX.Element;
  searchPlaceholder: string;
  isSearchPrivate?: boolean;
};
const Table = t0 => {
  const $ = _c(35);
  const {
    "aria-label": ariaLabel,
    columns,
    emptyStateText,
    searchPlaceholder,
    renderTr,
    renderMobileRow,
    isSearchPrivate
  } = t0;
  const {
    t
  } = useTranslation("common");
  const context = useEntitiesContext();
  const [search, setSearch] = useState(context.initialSearch ?? "");
  const debouncedSearch = useDebounce(search, 300);
  let t1;
  if ($[0] !== search) {
    t1 = search.trim();
    $[0] = search;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const trimmedSearch = t1;
  const isSearchTooShort = trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_LENGTH;
  const isMobile = useMobileLayout();
  let t2;
  if ($[2] !== context.errorMessage || $[3] !== emptyStateText || $[4] !== isSearchTooShort || $[5] !== t) {
    t2 = context.errorMessage || (isSearchTooShort ? t(_temp, {
      minLength: MIN_SEARCH_LENGTH
    }) : emptyStateText);
    $[2] = context.errorMessage;
    $[3] = emptyStateText;
    $[4] = isSearchTooShort;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const emptyText = t2;
  let t3;
  let t4;
  if ($[7] !== context.initialSearch) {
    t3 = () => {
      if (context.initialSearch !== undefined) {
        setSearch(context.initialSearch);
      }
    };
    t4 = [context.initialSearch];
    $[7] = context.initialSearch;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== context || $[11] !== debouncedSearch) {
    t5 = () => {
      context.onSearchChange?.(debouncedSearch);
    };
    $[10] = context;
    $[11] = debouncedSearch;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== debouncedSearch) {
    t6 = [debouncedSearch];
    $[13] = debouncedSearch;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useUpdateEffect(t5, t6);
  const t7 = isSearchPrivate ? "mask" : undefined;
  let t8;
  if ($[15] !== search || $[16] !== searchPlaceholder || $[17] !== t7) {
    t8 = <SearchInput autoCapitalize="off" autoComplete="off" autoCorrect="off" onChangeText={setSearch} placeholder={searchPlaceholder} size="compact" shape="pill" value={search} width="100%" containerClassName="w-full md:w-[330px]" data-dd-privacy={t7} />;
    $[15] = search;
    $[16] = searchPlaceholder;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Filters />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8) {
    t10 = <div className="flex flex-col gap-3 mb-3">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== ariaLabel || $[23] !== columns || $[24] !== context.data || $[25] !== context.isPending || $[26] !== context.onRowClick || $[27] !== emptyText || $[28] !== isMobile || $[29] !== renderMobileRow || $[30] !== renderTr) {
    t11 = isMobile && renderMobileRow ? <MobileList aria-label={ariaLabel} emptyStateText={emptyText} renderRow={renderMobileRow} /> : <UITable aria-label={ariaLabel} columns={columns} emptyStateText={emptyText} getKeyForRow={_temp2} isLoading={context.isPending} shimmerHeight="40px" items={context.data} onRowClick={context.onRowClick} renderTr={t12 => {
      const {
        item
      } = t12;
      return renderTr(item);
    }} />;
    $[22] = ariaLabel;
    $[23] = columns;
    $[24] = context.data;
    $[25] = context.isPending;
    $[26] = context.onRowClick;
    $[27] = emptyText;
    $[28] = isMobile;
    $[29] = renderMobileRow;
    $[30] = renderTr;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
export default Table;
function _temp($) {
  return $.components.entitiesTable.searchTooShort;
}
function _temp2(entity) {
  return entity.id;
}